import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus, Copy, Building2, Save } from "lucide-react";
import { Navbar, Footer } from "@/components/Chrome";
import { useAuth, startLogin } from "@/context/AuthContext";
import { api, money2, num, STATUS_LABEL, STATUS_COLOR, INDUSTRIES } from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Portal() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [profile, setProfile] = useState({ name: "", company_name: "", industry: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { document.title = "Mi portal — Spott"; }, []);

  useEffect(() => {
    if (!user) return;
    setProfile({
      name: user.name || "", company_name: user.company_name || "",
      industry: user.industry || "", phone: user.phone || "",
    });
    api.get("/quotes/mine").then(({ data }) => setQuotes(data));
  }, [user]);

  const duplicate = async (id) => {
    const { data } = await api.post(`/quotes/${id}/duplicate`);
    toast.success("Cotización duplicada como borrador");
    navigate(`/cotizar?retomar=${data.quote_id}`);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", profile);
      setUser(data);
      toast.success("Perfil actualizado");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-spott-light">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center">
          <div className="h-9 w-9 animate-spin rounded-full border-2 border-spott-yellow border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-spott-light">
        <Navbar />
        <div className="mx-auto max-w-md px-5 py-28 text-center" data-testid="portal-login-gate">
          <h1 className="text-2xl font-bold text-spott-blue sm:text-3xl">Accede a tu portal</h1>
          <p className="mt-3 text-sm text-gray-600">
            Inicia sesión con Google para ver el historial y estado de tus cotizaciones.
          </p>
          <button onClick={() => startLogin("/portal")} data-testid="portal-login-btn"
            className="mt-8 w-full rounded-full bg-spott-yellow py-4 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.02]">
            Continuar con Google
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const totalPipeline = quotes.reduce((a, q) => a + (q.total || 0), 0);

  return (
    <div className="min-h-screen bg-spott-light">
      <Navbar />
      <div className="relative overflow-hidden bg-spott-blue py-14 grain">
        <div className="arc-deco -right-20 -top-24 h-72 w-72" />
        <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-6 px-5 lg:px-10">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-spott-yellow">Portal del cliente</p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="portal-greeting">
              Hola, {user.name.split(" ")[0]}
            </h1>
            <p className="mt-2 text-sm font-light text-spott-mid">{user.email} · rol: {user.role}</p>
          </div>
          <Link to="/cotizar" data-testid="portal-new-quote-btn"
            className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-6 py-3.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.03]">
            <Plus size={17} /> Nueva cotización
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 pt-10 lg:px-10">
        <div className="grid gap-5 sm:grid-cols-2">
          <Link to="/campanas" data-testid="portal-campaigns-link"
            className="rounded-xl border border-spott-mid bg-white p-6 transition-transform hover:-translate-y-1">
            <h3 className="text-base font-semibold text-spott-blue">Reportería de campañas</h3>
            <p className="mt-1 text-sm text-gray-600">
              Estado de ejecución, timeline y desempeño de medios digitales de tus campañas.
            </p>
          </Link>
          <Link to="/recompensas" data-testid="portal-rewards-link"
            className="rounded-xl border border-spott-yellow bg-spott-yellow/10 p-6 transition-transform hover:-translate-y-1">
            <h3 className="text-base font-semibold text-spott-blue">Puntos Spott</h3>
            <p className="mt-1 text-sm text-gray-700">
              Acumula 1 punto por dólar ejecutado y cánjealos por espacios publicitarios.
            </p>
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
        <div className="grid gap-5 sm:grid-cols-3">
          {[
            { l: "Cotizaciones", v: quotes.length },
            { l: "Valor total cotizado", v: money2(totalPipeline) },
            { l: "En negociación", v: quotes.filter((q) => q.status === "en_negociacion").length },
          ].map((k) => (
            <div key={k.l} className="rounded-xl border border-spott-mid bg-white p-6">
              <div className="text-sm text-gray-500">{k.l}</div>
              <div className="mt-2 text-2xl font-bold text-spott-blue">{k.v}</div>
            </div>
          ))}
        </div>

        <h2 className="mt-12 text-xl font-bold text-spott-blue sm:text-2xl">Historial de cotizaciones</h2>
        <div className="mt-5 space-y-4" data-testid="quotes-list">
          {quotes.length === 0 && (
            <div className="rounded-xl border border-spott-mid bg-white p-10 text-center">
              <FileText className="mx-auto text-spott-mid" size={34} />
              <p className="mt-4 text-sm text-gray-600">Todavía no tienes cotizaciones guardadas.</p>
              <Link to="/cotizar" className="mt-5 inline-block rounded-full bg-spott-yellow px-6 py-3 text-sm font-bold text-spott-blue">
                Cotizar ahora
              </Link>
            </div>
          )}
          {quotes.map((q) => (
            <div key={q.quote_id} data-testid={`quote-row-${q.quote_id}`}
              className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-spott-mid bg-white p-6 transition-transform hover:-translate-y-0.5">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs text-gray-500">{q.quote_id}</span>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLOR[q.status]}`}>
                    {STATUS_LABEL[q.status]}
                  </span>
                </div>
                <h3 className="mt-2 text-base font-semibold text-spott-blue">
                  {q.company_name || "Sin marca"} · {q.space_items?.length || 0} espacios
                  {q.total_units ? ` (${q.total_units} unidades)` : ""}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {q.duration_months} mes(es) · {num(q.total_reach)} impactos · {q.created_at?.slice(0, 10)}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xl font-bold text-spott-blue">{money2(q.total)}</div>
                  <div className="text-xs text-gray-500">{money2(q.monthly_cost)}/mes</div>
                </div>
                <div className="flex flex-col gap-2">
                  <Link to={`/cotizacion/${q.quote_id}`} data-testid={`quote-view-${q.quote_id}`}
                    className="rounded-full bg-spott-blue px-5 py-2 text-xs font-bold text-white">Ver detalle</Link>
                  <div className="flex gap-2">
                    {q.status === "borrador" && (
                      <Link to={`/cotizar?retomar=${q.quote_id}`} data-testid={`quote-resume-${q.quote_id}`}
                        className="rounded-full border border-spott-mid px-4 py-2 text-xs font-semibold text-spott-blue">
                        Retomar
                      </Link>
                    )}
                    <button onClick={() => duplicate(q.quote_id)} data-testid={`quote-duplicate-${q.quote_id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-spott-mid px-4 py-2 text-xs font-semibold text-spott-blue">
                      <Copy size={12} /> Duplicar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-14 flex items-center gap-2 text-xl font-bold text-spott-blue sm:text-2xl">
          <Building2 size={20} className="text-spott-yellow" /> Datos de mi empresa
        </h2>
        <div className="mt-5 grid gap-5 rounded-xl border border-spott-mid bg-white p-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm text-spott-blue">Nombre de contacto</Label>
            <Input value={profile.name} data-testid="profile-name"
              onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-spott-blue">Empresa / Marca</Label>
            <Input value={profile.company_name} data-testid="profile-company"
              onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-spott-blue">Industria</Label>
            <select value={profile.industry} data-testid="profile-industry"
              onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
              className="h-10 w-full rounded-lg border border-spott-mid bg-white px-3 text-sm text-spott-blue">
              <option value="">Selecciona…</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm text-spott-blue">Teléfono</Label>
            <Input value={profile.phone} data-testid="profile-phone"
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <button onClick={saveProfile} disabled={saving} data-testid="profile-save-btn"
              className="inline-flex items-center gap-2 rounded-full bg-spott-yellow px-6 py-3 text-sm font-bold text-spott-blue disabled:opacity-60">
              <Save size={16} /> {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
